/**
 * Where the Create Client Review modal's Page URL field starts.
 *
 * Emily, 2026-09-03: "this should prefill with the last used url or the
 * projects default url". The field opened on a bare `https://` every time, so
 * the second link for the same page meant retyping the first one.
 *
 * Precedence, in order:
 *   1. the NEWEST client review on the project (by createdAt, so the answer
 *      does not depend on the list's order even though the server already
 *      sends newest-first, services/clientReview/reviews.ts);
 *   2. `https://` + the project's primary domain — the "URL" field in Edit
 *      Project (ProjectEditModal.tsx). Measured on production 2026-09-03:
 *      260 of 393 live projects carry one, against 2 with a scan URL, which
 *      is why scanUrl is not a rung here;
 *   3. the bare scheme the field always carried, so the submit guard that
 *      treats it as "empty" (ClientReviewCreateModal.tsx) keeps working.
 *
 * Pure: takes whatever the two queries hold (undefined while loading is
 * fine) and never throws, so the section can call it on every render.
 */

import { SANDBOX_DEMO_PAGE_URL } from '@overlayqa/shared';

/** The value the field opened on before this helper existed; still the "empty" sentinel. */
export const EMPTY_PAGE_URL = 'https://';

interface ReviewLike {
  pageUrl: string;
  createdAt: string;
}

interface ProjectLike {
  primaryDomain?: string | null;
}

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;
const SANDBOX_HOST = new URL(SANDBOX_DEMO_PAGE_URL).hostname;

function newestReviewUrl(reviews: ReadonlyArray<ReviewLike> | undefined): string | null {
  if (!reviews || reviews.length === 0) return null;
  let best: { url: string; at: number } | null = null;
  for (const review of reviews) {
    const url = (review.pageUrl ?? '').trim();
    if (!url) continue;
    const at = Date.parse(review.createdAt);
    const time = Number.isNaN(at) ? 0 : at;
    // Strictly greater: on a tie the earlier list position wins, which is the
    // server's own newest-first order.
    if (!best || time > best.at) best = { url, at: time };
  }
  return best?.url ?? null;
}

/** `https://` + the project's primary domain, or null. Shared with captureIssuesUrl.ts (Capture Issues). */
export function projectDefaultUrl(project: ProjectLike | undefined): string | null {
  const domain = (project?.primaryDomain ?? '').trim();
  if (!domain) return null;
  // The onboarding "SaaS Demo" chip stores the sandbox's HOST as the primary
  // domain (routes/onboarding.ts), so https:// + domain would be the API's
  // JSON root, which sends X-Frame-Options: DENY. Open the demo page it means.
  if (domain.toLowerCase() === SANDBOX_HOST) return SANDBOX_DEMO_PAGE_URL;
  return SCHEME_RE.test(domain) ? domain : `https://${domain}`;
}

export function resolveClientReviewInitialUrl(input: {
  reviews: ReadonlyArray<ReviewLike> | undefined;
  project: ProjectLike | undefined;
}): string {
  return newestReviewUrl(input.reviews) ?? projectDefaultUrl(input.project) ?? EMPTY_PAGE_URL;
}
